import { z } from 'zod';

import { PaginationSchema } from '../../pagination.js';

export const ListFiscalOperationDefinitionsQuerySchema = z.looseObject({
  documentKind: z.enum(['NFE', 'NFCE', 'NFSE', 'CTE', 'MDFE', 'EXTERNAL']).optional(),
  direction: z.enum(['INBOUND', 'OUTBOUND']).optional(),
  verticalScope: z.string().max(30).nullable().optional(),
});

export const FiscalOperationDefinitionItemSchema = z.looseObject({
  code: z.string(),
  name: z.string(),
  operationNature: z.string(),
  direction: z.string(),
  operationKind: z.string(),
  documentKind: z.string(),
  defaultCfopCode: z.string().nullable(),
  verticalScope: z.string().nullable(),
});

export const ListFiscalOperationDefinitionsResponseSchema = z.looseObject({
  items: z.array(FiscalOperationDefinitionItemSchema),
});

export const ListFiscalCfopQuerySchema = PaginationSchema.extend({
  direction: z.enum(['INBOUND', 'OUTBOUND']).optional(),
  search: z.string().max(100).optional(),
});

export const FiscalNcmItemSchema = z.looseObject({
  code: z.string(),
  description: z.string(),
  validFrom: z.iso.date(),
  validUntil: z.iso.date().nullable(),
});

export const ListFiscalNcmQuerySchema = PaginationSchema.extend({
  search: z.string().max(100).optional(),
});

export const ListFiscalNcmResponseSchema = z.looseObject({
  items: z.array(FiscalNcmItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export const CreateFiscalNcmBodySchema = z.looseObject({
  code: z.string().min(1).max(10),
  description: z.string().min(1).max(500),
  validFrom: z.iso.date().optional(),
});

export const UpdateFiscalNcmBodySchema = z.looseObject({
  description: z.string().min(1).max(500).optional(),
  validFrom: z.iso.date().optional(),
  validUntil: z.iso.date().nullable().optional(),
});

export const CloseFiscalNcmValidityBodySchema = z.looseObject({
  validUntil: z.iso.date().optional(),
});

export const FiscalNcmCodeParamsSchema = z.looseObject({
  code: z.string().min(1).max(10),
});

export const FiscalNcmMutationResponseSchema = FiscalNcmItemSchema;
export const FiscalLc116ItemSchema = z.looseObject({
  code: z.string(),
  description: z.string(),
});

export const ListFiscalLc116QuerySchema = PaginationSchema.extend({
  search: z.string().max(100).optional(),
});

export const ListFiscalLc116ResponseSchema = z.looseObject({
  items: z.array(FiscalLc116ItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export const FiscalCnaeItemSchema = z.looseObject({
  code: z.string(),
  description: z.string(),
});

export const ListFiscalCnaeQuerySchema = PaginationSchema.extend({
  search: z.string().max(100).optional(),
});

export const ListFiscalCnaeResponseSchema = z.looseObject({
  items: z.array(FiscalCnaeItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export const FiscalCestItemSchema = z.looseObject({
  code: z.string(),
  description: z.string(),
  ncmCode: z.string().nullable(),
});

export const ListFiscalCestQuerySchema = PaginationSchema.extend({
  search: z.string().max(100).optional(),
});

export const ListFiscalCestResponseSchema = z.looseObject({
  items: z.array(FiscalCestItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export const FiscalCstCatalogItemSchema = z.looseObject({
  code: z.string(),
  description: z.string(),
});

export const ListFiscalCstPisCofinsQuerySchema = PaginationSchema.extend({
  search: z.string().max(100).optional(),
});

export const ListFiscalCstPisCofinsResponseSchema = z.looseObject({
  items: z.array(FiscalCstCatalogItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export const ListFiscalCstIpiQuerySchema = PaginationSchema.extend({
  search: z.string().max(100).optional(),
});

export const ListFiscalCstIpiResponseSchema = z.looseObject({
  items: z.array(FiscalCstCatalogItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export const ListFiscalCstIbsCbsQuerySchema = PaginationSchema.extend({
  search: z.string().max(100).optional(),
});

export const ListFiscalCstIbsCbsResponseSchema = z.looseObject({
  items: z.array(FiscalCstCatalogItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export const ListFiscalIbsCbsClassificationQuerySchema = PaginationSchema.extend({
  search: z.string().max(100).optional(),
});

export const ListFiscalIbsCbsClassificationResponseSchema = z.looseObject({
  items: z.array(FiscalCstCatalogItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export const FiscalCfopItemSchema = z.looseObject({
  code: z.string(),
  description: z.string(),
  direction: z.string(),
  operationType: z.string(),
});

export const ListFiscalCfopResponseSchema = z.looseObject({
  items: z.array(FiscalCfopItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export type ListFiscalOperationDefinitionsQuery = z.infer<
  typeof ListFiscalOperationDefinitionsQuerySchema
>;
export type ListFiscalCfopQuery = z.infer<typeof ListFiscalCfopQuerySchema>;
export type ListFiscalNcmQuery = z.infer<typeof ListFiscalNcmQuerySchema>;
export type FiscalNcmItem = z.infer<typeof FiscalNcmItemSchema>;
export type CreateFiscalNcmBody = z.infer<typeof CreateFiscalNcmBodySchema>;
export type UpdateFiscalNcmBody = z.infer<typeof UpdateFiscalNcmBodySchema>;
export type CloseFiscalNcmValidityBody = z.infer<typeof CloseFiscalNcmValidityBodySchema>;
export type ListFiscalLc116Query = z.infer<typeof ListFiscalLc116QuerySchema>;
export type ListFiscalCnaeQuery = z.infer<typeof ListFiscalCnaeQuerySchema>;
export type ListFiscalCestQuery = z.infer<typeof ListFiscalCestQuerySchema>;
export type ListFiscalCstPisCofinsQuery = z.infer<typeof ListFiscalCstPisCofinsQuerySchema>;
export type ListFiscalCstIpiQuery = z.infer<typeof ListFiscalCstIpiQuerySchema>;
export type ListFiscalCstIbsCbsQuery = z.infer<typeof ListFiscalCstIbsCbsQuerySchema>;
export type ListFiscalIbsCbsClassificationQuery = z.infer<
  typeof ListFiscalIbsCbsClassificationQuerySchema
>;
